import { useState, useRef, useEffect } from "react"
import { Modal } from "react-bootstrap"
import { SensorIcon } from "../../icons/webSvg";
import { SensorUserPositions, SosPositionsList } from "../../services/authinterface";

type Sensor = {
    dx: string
    dy: string
    width: string   // original image width from API
    height: string  // original image height from API
    isSelected: boolean
    sensorUserPositions: SensorUserPositions[] | null
    relatedSensors: SosPositionsList[] | null
    name: string
}

type Props = {
    show: boolean
    onClose: () => void
    selectedImage: {
        planimage: string
        FloorplanSensor?: Sensor[]
    } | null
}

const SensorImageModel = ({ show, onClose, selectedImage }: Props) => {
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
    const containerRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (containerRef.current) {
            const updateSize = () => {
                if (!containerRef.current) return
                setContainerSize({
                    width: containerRef.current.clientWidth,
                    height: containerRef.current.clientHeight,
                })
            }

            updateSize()
            const resizeObserver = new ResizeObserver(updateSize)
            resizeObserver.observe(containerRef.current)

            return () => resizeObserver.disconnect()
        }
    }, [selectedImage])

    // Calculate aspect ratio from the first sensor's dimensions
    const getAspectRatio = () => {
        if (!selectedImage?.FloorplanSensor?.length) return 1;

        // Find sensor with the smallest width
        const smallestWidthSensor = selectedImage.FloorplanSensor.reduce((min, sensor) => {
            return parseFloat(sensor.width) < parseFloat(min.width) ? sensor : min;
        });

        const originalWidth = parseFloat(smallestWidthSensor.width);
        const originalHeight = parseFloat(smallestWidthSensor.height);

        return originalWidth / originalHeight;
    };

    const aspectRatio = getAspectRatio()

    return (
        <Modal
            className="comman-modal"
            show={show}
            onHide={onClose}
            centered
        >
            <Modal.Header closeButton className="comman-modal-header" />
            <Modal.Body className="comman-modal-body text-center">
                {selectedImage && selectedImage.planimage ? (
                    <div
                        ref={containerRef}
                        className="position-relative d-inline-block mx-auto"
                        style={{
                            width: "100%",
                            maxWidth: "800px",
                            aspectRatio: aspectRatio,
                            maxHeight: "80vh"
                        }}
                    >
                        <img
                            src={selectedImage.planimage}
                            alt="Preview"
                            className="img-fluid d-block w-100 h-100"
                            style={{ objectFit: "contain" }}
                        />

                        {selectedImage.FloorplanSensor?.map((sensor, index) => {
                            if (!sensor.dx || !sensor.dy) {
                                return null
                            }
                            const iconWidth = 35
                            const iconHeight = 35

                            if (!containerSize.width || !containerSize.height) return null

                            const originalWidth = parseFloat(sensor.width)
                            const originalHeight = parseFloat(sensor.height)

                            const dx = parseFloat(sensor.dx)
                            const dy = parseFloat(sensor.dy)

                            // Calculate position relative to container
                            const mappedDx = (dx / originalWidth) * containerSize.width;
                            const mappedDy = (dy / originalHeight) * containerSize.height;

                            const policeCount = sensor?.sensorUserPositions?.filter(item => item?.is_police)?.length || 0;
                            const userCount = sensor?.sensorUserPositions?.filter(item => !item?.is_police)?.length || 0;
                            const sosCount = sensor?.relatedSensors?.filter(item => item?.status === "1")?.length || 0;

                            return (
                                <>
                                    <div
                                        key={index}
                                        // className="position-absolute"
                                        style={{
                                            position: "absolute",
                                            left: `${mappedDx + (window.innerWidth < 600 ? 0 : 10)}px`,
                                            top: `${mappedDy + (window.innerWidth < 600 ? 0 : 15)}px`,
                                            width: `${iconWidth}px`,
                                            height: `${iconHeight}px`,
                                            zIndex: sensor.isSelected ? 12 : 11,
                                            pointerEvents: "auto"
                                        }}
                                        className="tooltip-custom"
                                        data-tooltip={sensor.name || `Sensor ${index + 1}`}
                                        
                                    >
                                        {sensor.isSelected === true ? <SensorIcon isActive={true} /> : <SensorIcon />}
                                    </div>
                                    {sosCount && sosCount > 0 ? (
                                        <div
                                            key={index}
                                            style={{
                                                position: "absolute",
                                                left: `${mappedDx - (window.innerWidth < 600 ? 35 : 25)}px`,
                                                top: `${mappedDy + 15}px`,
                                                width: `${iconWidth}px`,
                                                height: `${iconHeight}px`,
                                                zIndex: 10,
                                                pointerEvents: "auto",
                                            }}
                                        >
                                            <svg width="30" height="30" viewBox="0 0 100 120" xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="50" cy="50" r="50" fill="red" />
                                                <text
                                                    x="50"
                                                    y="45"
                                                    textAnchor="middle"
                                                    dominant-baseline="central"
                                                    fontSize="50"
                                                    // fontWeight="bold"
                                                    fill="white"
                                                >
                                                    {sosCount}
                                                </text>
                                            </svg>
                                        </div>
                                    ) : ""}
                                    {policeCount && policeCount > 0 ? <div
                                        key={index}
                                        style={{
                                            position: "absolute",
                                            left: `${mappedDx + 10}px`,
                                            top: `${mappedDy - (window.innerWidth < 600 ? 35 : 25)}px`,
                                            width: `${iconWidth}px`,
                                            height: `${iconHeight}px`,
                                            zIndex: 10,
                                            pointerEvents: "auto",
                                        }}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 120" width="30" height="30">
                                            <path d="M50 0 L95 20 V60 C95 90 75 110 50 120 C25 110 5 90 5 60 V20 Z" fill="#00AEEF" stroke="black" stroke-width="1" />
                                            <text
                                                x="50"
                                                y="50"
                                                textAnchor="middle"
                                                dominant-baseline="central"
                                                fontSize="50"
                                                // fontWeight="bold"
                                                fill="white"
                                            >
                                                {policeCount}
                                            </text>
                                        </svg>
                                    </div> : ""}
                                    {userCount && userCount > 0 ? <div
                                        key={index}
                                        style={{
                                            position: "absolute",
                                            left: `${mappedDx + (window.innerWidth < 600 ? 40 : 50)}px`,
                                            top: `${mappedDy + 15}px`,
                                            width: `${iconWidth}px`,
                                            height: `${iconHeight}px`,
                                            zIndex: 10,
                                            pointerEvents: "auto",
                                        }}

                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="30" height="30">
                                            <polygon points="50,0 100,50 50,100 0,50" fill="green" stroke="black" stroke-width="1" />
                                            <text
                                                x="50"
                                                y="50"
                                                textAnchor="middle"
                                                fontSize="40"
                                                dominant-baseline="central"
                                                // fontWeight="bold"
                                                fill="white"
                                            >
                                                {userCount}
                                            </text>
                                        </svg>
                                    </div> : ""}
                                </>
                            )
                        })}
                    </div>
                ) : (
                    <p>No image available</p>
                )}
            </Modal.Body>
        </Modal>
    )
}

export default SensorImageModel